// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logomark } from '../ui/Logomark.js';
import * as bip39 from 'bip39';
import { recoverAccount, getRecoverKeys } from '../../lib/syncClient.js';
import {
  deriveAuthTokenViaWorker,
  deriveRecoveryProofViaWorker,
  unwrapDekMnemonicViaWorker,
  buildKeyMaterialBundle,
} from '../../store/useCryptoWorker.js';
import { useApp } from '../../store/AppContext.js';
import { evaluatePassword } from '../../lib/passwordStrength.js';
import { ServerUrlField } from './ServerUrlField.js';

function mnemonicToEntropyBytes(mnemonic: string): Uint8Array {
  const hex = bip39.mnemonicToEntropy(mnemonic);
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

interface RecoveryFormProps {
  syncUrl: string;
  worker: Worker;
  onSwitchToLogin: () => void;
}

export function RecoveryForm({ syncUrl, worker, onSwitchToLogin }: RecoveryFormProps) {
  const { t } = useTranslation(['auth', 'common']);
  const { dispatch } = useApp();

  const [username, setUsername] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showMnemonic, setShowMnemonic] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');

  const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle';
  const labelCls = 'block text-sm text-text-muted mb-1';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!username.trim()) { setError(t('register.usernameRequired')); return; }
    if (!mnemonic.trim()) { setError(t('recovery.phraseRequired')); return; }
    const strength = evaluatePassword(newPassword);
    if (!strength.ok) { setError(t(`passwordStrength.${strength.messageKey}`, { ...strength.messageParams })); return; }
    if (newPassword !== confirmPassword) { setError(t('register.passwordsMismatch')); return; }

    const trimmed = mnemonic.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!bip39.validateMnemonic(trimmed)) {
      setError(t('recovery.invalidPhrase'));
      return;
    }

    setLoading(true);
    try {
      const entropy = mnemonicToEntropyBytes(trimmed);

      setLoadingStep(t('register.derivingRecoveryProof'));
      const recoveryProof = await deriveRecoveryProofViaWorker(worker, entropy, username.trim());

      setLoadingStep(t('login.fetchingKeys'));
      // Recover the existing DEK by unwrapping it with the mnemonic — this keeps
      // the existing encrypted ledger decryptable under the new password.
      const bundle = await getRecoverKeys(syncUrl, username.trim(), recoveryProof);
      const keyData = await unwrapDekMnemonicViaWorker(worker, bundle.wrappedByMnemonic, entropy);

      setLoadingStep(t('recovery.derivingNewAuth'));
      const newServerAuthToken = await deriveAuthTokenViaWorker(worker, newPassword, username.trim());

      setLoadingStep(t('recovery.reencryptingKeys'));
      const newBundle = await buildKeyMaterialBundle(worker, keyData, newPassword, entropy, trimmed);

      setLoadingStep(t('recovery.recoveringAccount'));
      const result = await recoverAccount(
        syncUrl,
        username.trim(),
        recoveryProof,
        newServerAuthToken,
        newBundle,
        recoveryProof,
      );

      dispatch({
        type: 'AUTH_SUCCESS',
        username: username.trim(),
        token: result.token,
        refreshToken: result.refreshToken,
        keyData,
        syncUrl,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recovery.recoveryFailed'));
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-2xl font-semibold text-text mb-1">
            <Logomark size={24} />
            {t('common:appName')}
          </div>
          <p className="text-sm text-text-muted">{t('common:tagline')}</p>
        </div>

        <div className="bg-bg-surface border border-border p-6">
          <h2 className="text-md font-semibold text-text mb-2">{t('recovery.title')}</h2>
          <p className="text-sm text-text-muted mb-5">
            {t('recovery.prompt')}
          </p>

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelCls}>{t('login.username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={inputCls}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-text-muted">{t('recovery.recoveryPhrase')}</label>
                <button
                  type="button"
                  onClick={() => setShowMnemonic(!showMnemonic)}
                  className="text-xs text-accent hover:text-accent-hover"
                >
                  {showMnemonic ? t('common:actions.hide') : t('common:actions.show')}
                </button>
              </div>
              <textarea
                value={mnemonic}
                onChange={(e) => setMnemonic(e.target.value)}
                rows={showMnemonic ? 4 : 2}
                className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none font-mono resize-none placeholder-text-subtle"
                placeholder={t('recovery.phrasePlaceholder')}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            </div>

            <div>
              <label className={labelCls}>{t('recovery.newPassword')}</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={inputCls}
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className={labelCls}>{t('recovery.confirmNewPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputCls}
                autoComplete="new-password"
              />
            </div>

            <ServerUrlField />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-white py-2.5 text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
            >
              {loading ? (loadingStep || t('recovery.recovering')) : t('recovery.title')}
            </button>
          </form>
        </div>

        <div className="mt-4 text-sm text-text-muted px-1">
          <button onClick={onSwitchToLogin} className="hover:text-text transition-colors">
            {t('recovery.rememberPassword')}
          </button>
        </div>
      </div>
    </div>
  );
}
